import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Users, CalendarClock, LogOut, Settings, FileText, Tag, Upload, UserPlus, BookOpen, PlusCircle, Menu, X, ClipboardList, FileCheck
} from "lucide-react";

const ROLE_LABELS = { ADMIN: "Administrateur", RESPONSABLE: "Responsable", COACH: "Coach" };

const navItems = [
  { to: "/inscription", label: "Inscription", icon: UserPlus, minRole: "COACH" },
  { to: "/cash-entry", label: "Entrée Caisse", icon: PlusCircle, minRole: "COACH" },
  { to: "/prestations", label: "Mes Prestations", icon: ClipboardList, minRole: "COACH" },
  { to: "/mutuelle", label: "Fiche Mutuelle", icon: FileCheck, minRole: "COACH" },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, minRole: "RESPONSABLE" },
  { to: "/members", label: "Membres", icon: Users, minRole: "RESPONSABLE" },
  { to: "/expirations", label: "Échéances", icon: CalendarClock, minRole: "RESPONSABLE" },
  { to: "/reports", label: "Rapports", icon: FileText, minRole: "RESPONSABLE" },
  { to: "/catalog", label: "Catalogue", icon: Tag, minRole: "RESPONSABLE" },
  { to: "/cashbook", label: "Livre de Caisse", icon: BookOpen, minRole: "ADMIN" },
  { to: "/prestations-dashboard", label: "Prestations Coachs", icon: ClipboardList, minRole: "RESPONSABLE" },
  { to: "/import", label: "Import CSV", icon: Upload, minRole: "ADMIN" },
];

const ROLE_LEVELS = { COACH: 1, RESPONSABLE: 2, ADMIN: 3 };

const CRMLayout = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const userLevel = ROLE_LEVELS[user?.role] || 1;

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="crm-layout">
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#1a2744] text-white px-4 py-3 flex items-center justify-between shadow-md">
        <div className="flex items-center gap-3">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 rounded-lg hover:bg-white/10 transition-colors" data-testid="mobile-menu-toggle">
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <span className="text-[#e8d44d] font-bold text-lg">B.health</span>
          <span className="text-[#8fa4b5] text-sm">CRM</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#3899ec] flex items-center justify-center text-white text-sm font-medium">
            {user?.name?.[0] || user?.email?.[0] || "U"}
          </div>
        </div>
      </div>

      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setSidebarOpen(false)} data-testid="sidebar-overlay" />
      )}

      <aside className={`crm-sidebar ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        <div className="crm-sidebar-header">
          <img src="https://customer-assets.emergentagent.com/job_wix-form-replica/artifacts/8c7fc1k3_logofinal-logo-full-color-rgb-900px-w-72ppi.png" alt="B.health" className="crm-sidebar-logo" />
          <span className="crm-sidebar-title">CRM</span>
          <button onClick={() => setSidebarOpen(false)} className="sidebar-close-btn" data-testid="close-sidebar">
            <X size={20} />
          </button>
        </div>
        <nav className="crm-nav">
          {navItems.map(({ to, label, icon: Icon, minRole }) => {
            const minLevel = ROLE_LEVELS[minRole] || 1;
            if (userLevel < minLevel) return null;
            return (
              <NavLink key={to} to={to} className={({ isActive }) => `crm-nav-item ${isActive ? "crm-nav-active" : ""}`} onClick={() => setSidebarOpen(false)}>
                <Icon size={18} />
                <span>{label}</span>
              </NavLink>
            );
          })}
          {isAdmin && (
            <NavLink to="/settings" className={({ isActive }) => `crm-nav-item ${isActive ? "crm-nav-active" : ""}`} onClick={() => setSidebarOpen(false)}>
              <Settings size={18} />
              <span>Paramètres</span>
            </NavLink>
          )}
        </nav>
        <div className="crm-sidebar-footer">
          <div className="crm-user-info">
            <div className="crm-user-avatar">{user?.name?.[0] || user?.email?.[0] || "U"}</div>
            <div className="crm-user-details">
              <span className="crm-user-name">{user?.name || user?.email}</span>
              <span className="crm-user-role">{ROLE_LABELS[user?.role] || user?.role}</span>
            </div>
          </div>
          <button onClick={handleLogout} className="crm-logout-btn">
            <LogOut size={16} />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>
      
      <main className="crm-main pt-14 lg:pt-0">
        <Outlet />
      </main>
    </div>
  );
};

export default CRMLayout;
